import { QueryKey, useQueries } from '@tanstack/react-query'

import { withImport } from '~/store/middleware/withImport'

import { combineQueries } from '../shared/queryHelpers'
import { useSentryLogging } from '../shared/useSentryLogging'

type ImportEntity = ReturnType<ReturnType<typeof withImport>>

export type FetchQuery = {
    queryFn: () => ImportEntity
    queryKey: QueryKey
}

export type MultiQueryOptions = {
    skip?: boolean
    refetchInterval?: number | false
    refetchOnWindowFocus?: boolean
    refetchOnMount?: boolean
    staleTime?: number
    showToast?: boolean
}

/**
 * Fetch one or more entities and put them in the store.
 *
 * @param fetchQueries The list of import functions and their queryKey.
 * @param options Object with skip: skip the import, refetchInterval: interval in ms to refetch
 * @returns Object with isLoading: initial fetch, isFetching: every fetch, isError, isSuccess, and data
 */
export function useMultiQuery(fetchQueries: FetchQuery[], options?: MultiQueryOptions) {
    const queryResults = useQueries({
        queries: fetchQueries.map(({ queryFn, queryKey }) => ({
            queryFn,
            queryKey,
            enabled: !options?.skip,
            refetchInterval: options?.refetchInterval,
            refetchOnWindowFocus: options?.refetchOnWindowFocus,
            refetchOnMount: options?.refetchOnMount,
            staleTime: options?.staleTime,
        })),
        combine: combineQueries,
    })

    useSentryLogging({ key: 'useMultiQuery', queryResult: queryResults, toastNotification: { showToast: options?.showToast ?? true } })

    return queryResults
}
